<template>
  <div
    id="calibDetails"
    class="modal fade show"
    data-bs-keyboard="false"
    data-bs-backdrop="static"
    tabindex="-1"
    aria-labelledby="calibModalLabel"
    style="display: block;"
    aria-modal="true"
    role="dialog"
  >
    <div
      class="mt-6 modal-dialog modal-lg"
      role="document"
    >
      <div class="border-0 modal-content">
        <div class="top-0 z-1 position-absolute me-3 mt-3 end-0">
          <button
            class="d-flex flex-center transition-base btn-close btn btn-sm btn-circle"
            aria-label="Close"
            @click="handleClose"
          />
        </div>

        <div class="bg-body-tertiary py-3 ps-4 pe-6 rounded-top-3">
          <h4
            id="calibModalLabel"
            class="mb-1"
          >
            센서 보정
          </h4>
          <p class="mb-0 fs-11">
            디바이스 이름 : <a
              class="link-600 fw-semi-bold"
              href="#!"
            >{{ setdevice.devicename }}</a>
          </p>
        </div>

        <div class="px-card pt-1 pb-card modal-body fs--1">
          <div class="mt-3 media">
            <div class="media-body">
              <div
                v-if="deviceConfig"
                class="text-800 fs--1"
              >
                <div class="d-flex align-items-center modal_div">
                  <h6 class="me-2 mb-0 device-title">
                    디바이스 아이디
                  </h6>
                  <span class="mb-0">{{ setdevice.deviceid }}</span>
                </div>

                <div class="d-flex align-items-center modal_div">
                  <h6 class="me-2 mb-0 device-title">
                    디바이스 이름
                  </h6>
                  <span class="mb-0">{{ setdevice.devicename }}</span>
                </div>

                <div class="d-flex align-items-center modal_div">
                  <h6 class="me-2 mb-0 device-title">
                    설정 적용 상태
                  </h6>
                  <span
                    class="badge"
                    :class="deviceConfig.apply === 1 ? 'badge-soft-success' : 'badge-soft-warning'"
                  >
                    {{ deviceConfig.apply === 1 ? '적용됨' : '적용전' }}
                  </span>
                </div>

                <div class="d-flex align-items-center modal_div">
                  <h6 class="me-2 mb-0 device-title">
                    보정 센서 선택
                  </h6>
                  <select
                    v-model="selectedSensor"
                    class="form-select form-select-sm"
                    style="width:200px"
                  >
                    <option
                      v-for="sv in svList"
                      :key="sv.protocol"
                      :value="sv.protocol"
                    >
                      {{ sv.sname }}
                    </option>
                  </select>
                </div>

                <div class="d-flex align-items-center modal_div">
                  <input
                    id="cchecked"
                    v-model="calibEnabled"
                    class="form-check-input me-2"
                    type="checkbox"
                  >
                  <h6 class="me-3 mb-0">
                    보정 값 설정
                  </h6>

                  <h6 class="me-1 mb-0 device-label">
                    min
                  </h6>
                  <input
                    v-model.number="calibMin"
                    type="number"
                    class="form-control form-control-sm me-2"
                    style="width:70px"
                    :disabled="!calibEnabled"
                  >

                  <h6 class="me-1 mb-0 device-label">
                    med
                  </h6>
                  <input
                    v-model.number="calibMed"
                    type="number"
                    class="form-control form-control-sm me-2"
                    style="width:70px"
                    :disabled="!calibEnabled"
                  >

                  <h6 class="me-1 mb-0 device-label">
                    max
                  </h6>
                  <input
                    v-model.number="calibMax"
                    type="number"
                    class="form-control form-control-sm"
                    style="width:70px"
                    :disabled="!calibEnabled"
                  >
                </div>

                <div class="d-flex align-items-center modal_div">
                  <input
                    id="resetchecked"
                    v-model="resetCalib"
                    class="form-check-input me-2"
                    type="checkbox"
                  >
                  <h6 class="mb-0">
                    리셋
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <a
            class="btn btn-falcon-default btn-sm"
            @click="handleApply"
          >
            <span class="mr-2 fs--2" data-fa-i2svg="" />적용
          </a>
          <a
            class="btn btn-falcon-default btn-sm"
            @click="handleReload"
          >
            <span class="mr-2 fs--2" data-fa-i2svg="" />새로고침
          </a>
          <a
            class="btn btn-falcon-default btn-sm"
            @click="handleOk"
          >
            <span class="mr-2 fs--2" data-fa-i2svg="" />확인
          </a>
          <a
            class="btn btn-falcon-default btn-sm"
            @click="handleClose"
          >
            <span class="mr-2 fs--2" data-fa-i2svg="" />취소
          </a>
        </div>
      </div>
    </div>
  </div>

  <AlertModal ref="alertModalRef" />
</template>

<script setup lang="ts">
import type { Device, DeviceConfig, AjaxResult } from '@/types/commontypes'
import { onMounted, ref, computed, watch } from 'vue'
import AlertModal from '@/components/AlertModal.vue'

const emit = defineEmits(['close'])

const props = defineProps<{
  setdevice: Device
}>()

const deviceConfig = ref<DeviceConfig>({} as DeviceConfig)
const alertModalRef = ref()

const selectedSensor = ref<string>('')
const calibEnabled = ref(false)
const calibMin = ref(0)
const calibMed = ref(0)
const calibMax = ref(0)
const resetCalib = ref(false)

const defaultDeviceConfig: DeviceConfig = {
  deviceid: '',
  regdt: '',
  config: '',
  configjson: { set_interval: 0, set_gauge: 0, set_sensor: {} },
  ucount: 0,
  configuse: 0,
  apply: 0,
  alertconfig: '',
  alertconfigjson: {
    alert: false,
    dataalert: false,
    regularalert: false,
    regularalertinterval: 0,
    regularalertstarttime: 0,
    regularalertendtime: 0,
    sendsms: false,
    recvlist: '',
  },
}

const svList = computed(() => {
  if (!props.setdevice.svlist) return []
  try {
    const parsed = JSON.parse(props.setdevice.svlist)
    return Object.values(parsed).map((sv: any) => ({
      protocol: sv.protocol,
      sname: sv.sname,
    }))
  } catch {
    return []
  }
})

onMounted(() => {
  loadDeviceConfig()
})

watch(
  () => props.setdevice,
  () => { loadDeviceConfig() },
  { deep: true }
)

function loadDeviceConfig() {
  const data = JSON.stringify({ operator: 'loadDeviceConfig', deviceid: props.setdevice.deviceid })

  const xhr = new XMLHttpRequest()
  xhr.open('POST', '/ajax/ajaxcall')
  xhr.setRequestHeader('Content-type', 'application/json')
  xhr.send(data)

  xhr.addEventListener('load', () => {
    const result = JSON.parse(xhr.responseText)
    if (result && result.err === 0) {
      deviceConfig.value = result.data
      deviceConfig.value.configjson = JSON.parse(deviceConfig.value.config)
      const alertconfig_temp = deviceConfig.value.alertconfig.replace(/[\r\n]+/g, '\\n')
      deviceConfig.value.alertconfigjson = JSON.parse(alertconfig_temp)
    } else {
      deviceConfig.value = { ...defaultDeviceConfig }
      deviceConfig.value.configjson = { set_interval: 0, set_gauge: 0, set_sensor: {} }
    }
    applyConfigToForm()
  })
}

function applyConfigToForm() {
  const cjson = deviceConfig.value.configjson
  calibEnabled.value = cjson.calib === true
  selectedSensor.value = cjson.calib_item ?? (svList.value[0]?.protocol ?? '')
  calibMin.value = cjson.calib_min ?? 0
  calibMed.value = cjson.calib_med ?? 0
  calibMax.value = cjson.calib_max ?? 0
  resetCalib.value = cjson.reset_calib === true
}

function saveCalib(closeAfter: boolean) {
  if (!deviceConfig.value) return

  const config = { ...deviceConfig.value.configjson }
  config.calib = calibEnabled.value

  if (calibEnabled.value) {
    config.calib_item = selectedSensor.value
    config.calib_min = calibMin.value
    config.calib_med = calibMed.value
    config.calib_max = calibMax.value
    config.reset_calib = resetCalib.value
  } else {
    delete config.calib_item
    delete config.calib_min
    delete config.calib_med
    delete config.calib_max
    delete config.reset_calib
  }

  const data = JSON.stringify({
    operator: 'setDeviceCalib',
    deviceid: props.setdevice.deviceid,
    config,
  })

  const xhr = new XMLHttpRequest()
  xhr.open('POST', '/ajax/ajaxcall')
  xhr.setRequestHeader('Content-type', 'application/json')
  xhr.send(data)

  xhr.addEventListener('load', () => {
    const result: AjaxResult = JSON.parse(xhr.responseText)
    if (result.err === 0) {
      if (closeAfter) {
        emit('close')
      } else {
        loadDeviceConfig()
      }
    } else {
      alertModalRef.value.showAlertModal('경고', `센서보정 설정이 실패 했습니다. ${result.err}`)
    }
  })
}

function reloadConfig() {
  const data = JSON.stringify({ operator: 'loadDeviceConfig', deviceid: props.setdevice.deviceid })

  const xhr = new XMLHttpRequest()
  xhr.open('POST', '/ajax/ajaxcall')
  xhr.setRequestHeader('Content-type', 'application/json')
  xhr.send(data)

  xhr.addEventListener('load', () => {
    const result = JSON.parse(xhr.responseText)
    if (result && result.err === 0) {
      deviceConfig.value.apply = result.data.apply
    }
  })
}

const handleApply = () => saveCalib(false)
const handleOk = () => saveCalib(true)
const handleReload = () => reloadConfig()
const handleClose = () => emit('close')
</script>

<style>
</style>
